import { createAsync, query } from "@solidjs/router";
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  Suspense,
} from "solid-js";
import {
  getHomeTataRouteData,
  HomeTataRouteData,
} from "./data/home_tata_route_data";
import { tdV3DesktopBg, tdV3MobileBg } from "~/assets/assets";
import occasionsData from "~/data/groups/occasions.json";
import recipientsData from "~/data/groups/recipients.json";
import { useIsRouting, useNavigate, useSearchParams } from "@solidjs/router";
import { Spacer } from "~/widgets/spacer";
import { Product } from "~/server/types/search";
import { DottedLoader } from "~/widgets/loader";
import LocalStorageUtil from "~/utils/local_storage";
import { LocalStorageKey } from "~/types";
import { useModal } from "~/components/modal";
import { BottomBranding$Client } from "~/components/partner_components/partner_bottom_page_branding";
import { CategoryStrip } from "~/components/home/category_card";
import { ViewOfferDetails } from "~/components/webstore/view_offer_details";
import HubbleImage from "~/widgets/hubble_image";
import {
  TdExlusiveOfferText,
  TDOfferConstructText,
} from "~/components/partner_components/td_offer_construct_text";
import { useWebStoreContext } from "~/context/webstore_context";
import { MagnifyingGlass } from "~/assets/svg_icons/svg_icons";
import { useAnalyticsService } from "~/context/analytics_service_context";

const topBrandsForTataDigital = [
  "amazon-pay",
  "amazon",
  "flipkart",
  "zomato",
  "swiggy",
  "uber",
  "zepto",
  "blinkit",
  "bigbasket",
  "ola",
  "bookmyshow",
  "pvr",
];
function getBrandsAsPerTdOrder(brands: Product[]) {
  const mapByBrandKey = new Map<string, Product>();
  brands.forEach((brand) => {
    mapByBrandKey.set(brand.brandKey, brand);
  });

  let brandsInTdOrder: Product[] = [];

  topBrandsForTataDigital.forEach((brandKey) => {
    const brand = mapByBrandKey.get(brandKey);
    if (brand) {
      brandsInTdOrder.push(brand);
    }
  });

  brands.forEach((brand) => {
    if (!brandsInTdOrder.some((b) => b.brandKey === brand.brandKey)) {
      brandsInTdOrder.push(brand);
    }
  });

  return brandsInTdOrder;
}

const getHomeTataRouteData$C = query(getHomeTataRouteData, "home_tata");

export default function TataDigitalHomeComponent() {
  const routeData: Accessor<HomeTataRouteData | undefined> = createAsync(
    () => getHomeTataRouteData$C(),
    { deferStream: true }
  );

  return (
    <Suspense fallback={<DottedLoader />}>
      <Show when={routeData()}>
        <TataDigitalHomeContent
          routeData={routeData as Accessor<HomeTataRouteData>}
        />
      </Show>
    </Suspense>
  );
}

function TataDigitalHomeContent(props: {
  routeData: Accessor<HomeTataRouteData>;
}) {
  const [showCategoryGrid, setShowCategoryGrid] = createSignal(true);
  const isRouting = useIsRouting();

  const { openModal } = useModal()!;
  const analyticsService = useAnalyticsService();
  const { storeState, getClientId } = useWebStoreContext();

  const [queryParams, _setQueryParams] = useSearchParams();
  let maxBrands = parseInt((queryParams.maxBrands as string) ?? "0");

  const allBrands: Accessor<Product[]> = createMemo(() => {
    const brands = props.routeData().brands;

    if (maxBrands > 0) {
      return brands.slice(0, maxBrands);
    }

    return getBrandsAsPerTdOrder(brands);
  });

  let categoryObserver: IntersectionObserver;
  let observerCategoryGridRef: HTMLDivElement | undefined = undefined;

  const homepageZIndexes = {
    bottomStickyBranding: 20,
    brandsList: 10,
  };

  onMount(() => {
    if (props.routeData().offerConstruct?.subscriptionRewardDetails) {
      const offerConstruct = props.routeData().offerConstruct;
      const userId = storeState.v4HomePage?.user?.id;
      const modalShownLocalStorageKey = `${LocalStorageKey.OfferConstructModalShown}${userId}`;
      const modalShown = LocalStorageUtil.getItem<string>(
        modalShownLocalStorageKey
      );

      if (modalShown !== "1") {
        openModal(
          () => (
            <ViewOfferDetails
              offerConstruct={offerConstruct}
              playLottie={true}
            />
          ),
          "md:!min-w-[566px] overflow-hidden !max-w-[566px] !p-0",
          "w-[566px] overflow-hidden"
        );
        LocalStorageUtil.setItem(modalShownLocalStorageKey, "1");
      }
    }

    categoryObserver = new IntersectionObserver((entries) => {
      if (
        entries[0].boundingClientRect.height === 0 &&
        entries[0].boundingClientRect.width === 0
      ) {
        return;
      }
      if (entries[0].isIntersecting) {
        setShowCategoryGrid(true);
      } else {
        setShowCategoryGrid(false);
      }
    });

    onCleanup(() => {
      if (observerCategoryGridRef) {
        categoryObserver.unobserve(observerCategoryGridRef);
      }
    });
  });

  createEffect(() => {
    if (!isRouting()) {
      if (observerCategoryGridRef) {
        categoryObserver.observe(observerCategoryGridRef);
      }
    }
  });

  return (
    <div class="relative flex h-max  flex-col items-center justify-start gap-4">
      {/* <Show when={allBrands()} fallback={<DottedLoader color="#999" />}> */}
      <div
        class={`absolute top-0 z-[-10] flex w-full sm:bottom-[50px] ${"h-[200px] sm:h-[338px]"} bg-[image:var(--mobile-image-url)] bg-cover bg-center sm:bg-[image:var(--desktop-image-url)]`}
        style={{
          "background-size": "100% 100%",
          "--mobile-image-url": `url(${tdV3MobileBg})`,
          "--desktop-image-url": `url(${tdV3DesktopBg})`,
        }}
      ></div>
      <div class="td-desktop-ui mt-[40px] hidden w-full flex-col items-center justify-start sm:flex">
        <DesktopUI />
      </div>
      <div class="td-mobile-ui mt-7 flex w-full flex-col items-center justify-start px-3 md:hidden">
        <MobileUI />
      </div>
      <div class="mt-[20px]">&nbsp;</div>
      <BottomBranding$Client clientId={getClientId} />
      {/* </Show> */}
    </div>
  );

  function MobileUI() {
    return (
      <>
        <TdExlusiveOfferText />
        <Spacer height={12} />
        <span class="w-full text-center font-jakartaSans text-[28px] font-medium leading-[32px] tracking-[-0.48px] sm:text-[36px] md:font-semibold md:leading-[36px] md:tracking-normal">
          <TDOfferConstructText
            type={props.routeData()?.offerConstruct?.type}
            oneTimeRewardDetails={
              props.routeData()?.offerConstruct?.oneTimeRewardDetails
            }
            subscriptionRewardDetails={
              props.routeData()?.offerConstruct?.subscriptionRewardDetails
            }
            description={props.routeData()?.offerConstruct?.description}
          />
        </span>
        <div class="mt-[16px]"></div>
        <ViewOffersPill />
        <Spacer height={24} />
        <CategoriesForMobile />
        <Spacer height={24} />
        <GetGiftCardText text="Top brands" />
        <Spacer height={20} />
        <BrandsListWithHigherBrandsPerRow />
      </>
    );
  }

  function DesktopUI() {
    return (
      <>
        <TdExlusiveOfferText />
        <Spacer height={8} />
        <span class="z-10  max-w-[592px] text-center font-jakartaSans text-[36px]  font-medium leading-normal tracking-[-0.72px]">
          <TDOfferConstructText
            type={props.routeData()?.offerConstruct?.type}
            oneTimeRewardDetails={
              props.routeData()?.offerConstruct?.oneTimeRewardDetails
            }
            subscriptionRewardDetails={
              props.routeData()?.offerConstruct?.subscriptionRewardDetails
            }
            description={props.routeData()?.offerConstruct?.description}
          />
        </span>
        <div class="mt-[16px]"></div>
        <ViewOffersPill />
        <div class="mt-[12px]"></div>
        <CategoriesForDesktop />
        <div class="mt-6"></div>
        <GetGiftCardText text="Top brands" />
        <div class="mt-5"></div>
        <BrandsListWithHigherBrandsPerRow />
      </>
    );
  }

  function BrandsListWithHigherBrandsPerRow() {
    const [showAllBrands, setShowAllBrands] = createSignal(false);
    const navigate = useNavigate();

    const initialBrandsCount = topBrandsForTataDigital.length;

    const displayedBrands = createMemo(() => {
      if (showAllBrands()) {
        return allBrands();
      }
      return allBrands().slice(0, initialBrandsCount);
    });

    return (
      <div class="flex w-full flex-col items-center">
        <div
          class={`grid w-full grid-cols-3 justify-items-center gap-y-4 px-1 sm:w-fit sm:gap-8 md:grid-cols-4 lg:grid-cols-6`}
          style={{
            "z-index": homepageZIndexes.brandsList,
          }}
        >
          <For each={displayedBrands()}>
            {(brand) => (
              <div
                id={`brand-card-${brand.id}`}
                onClick={() => {
                  navigate(`/buy-gift-card/${brand.id}`);
                }}
                class="flex w-[106px] cursor-pointer flex-col items-start justify-center sm:w-[160px]"
              >
                <HubbleImage
                  src={brand.voucherProduct.iconImageUrl}
                  alt={brand.voucherProduct.title}
                  class="aspect-[16/18] h-full w-[106px] sm:w-[160px]"
                />
              </div>
            )}
          </For>
        </div>
        <Show
          when={!showAllBrands() && allBrands().length > initialBrandsCount}
        >
          <button
            onClick={() => {
              analyticsService.logEvent("click_see_more_brands");
              setShowAllBrands(true);
            }}
            class="mt-8 rounded-full border border-[#313538] bg-baseLight px-4 py-[6px] text-buttonMedium hover:border-[#ABABAB]"
          >
            See more Brands
          </button>
        </Show>
      </div>
    );
  }

  function CategoriesForMobile() {
    const { setIsSearchActive } = useWebStoreContext();
    return (
      <>
        <div
          class={`fixed top-[52px] w-full overflow-y-visible`}
          style={{
            "z-index": 20,
          }}
        >
          <Show
            when={
              !showCategoryGrid() &&
              props.routeData()?.categories &&
              props.routeData()?.categories != null
            }
          >
            <CategoryStrip
              categories={props.routeData()!.categories.data}
              maxCategoriesOnStrip={Math.min(
                10,
                props.routeData()!.categories.data.length
              )}
              scroll={true}
              border={false}
              occasions={occasionsData.occasions}
              recipients={recipientsData.recipients}
            />
          </Show>
        </div>
        <div ref={observerCategoryGridRef} class="z-20 w-full px-4">
          <div
            class="  flex flex-col gap-2 overflow-clip rounded-2xl  p-[6px]"
            style={{
              "background-color": "rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              onclick={() => {
                setIsSearchActive(true);
              }}
              class="flex w-full flex-row items-center justify-center gap-3 rounded-xl border border-baseTertiaryDark bg-baseTertiaryLight  py-2 text-normal text-textNormal"
            >
              <MagnifyingGlass class="text-textNormal" />
              search for brands
            </div>
            <div
              class="rounded-[10px] border border-baseTertiaryDark  bg-baseTertiaryLight"
              style={{ "box-shadow": "0px 2px 11px 0px #0000000D" }}
            >
              <CategoryStrip
                categories={props.routeData()!.categories.data}
                maxCategoriesOnStrip={Math.min(
                  10,
                  props.routeData()!.categories.data.length
                )}
                scroll={true}
                border={false}
                occasions={occasionsData.occasions}
                recipients={recipientsData.recipients}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  function CategoriesForDesktop() {
    return (
      <div
        class={`sticky top-[72px] bg-baseTertiaryLight pt-3`}
        style={{
          "z-index": 20,
        }}
      >
        <CategoryStrip
          categories={props.routeData()!.categories.data}
          maxCategoriesOnStrip={Math.min(
            10,
            props.routeData()!.categories.data.length
          )}
          scroll={false}
          border={true}
          occasions={occasionsData.occasions}
          recipients={recipientsData.recipients}
        />
      </div>
    );
  }

  function ViewOffersPill() {
    const { openModal } = useModal()!;
    return (
      <div
        class="z-10 flex h-9 cursor-pointer items-center justify-center rounded-full border border-[#313538] bg-baseLight px-4 py-[9.5px] text-buttonMedium hover:border-[#ABABAB]"
        onClick={() => {
          analyticsService.logEvent("click_view_offer_details_home_page");
          openModal(
            () => (
              <ViewOfferDetails
                offerConstruct={props.routeData()?.offerConstruct}
                playLottie={false}
              />
            ),
            "md:!min-w-[566px] overflow-hidden !max-w-[566px] !p-0",
            "w-[566px] overflow-hidden"
          );
        }}
      >
        View offer details
      </div>
    );
  }
}

function GetGiftCardText(props: { text?: string }) {
  return (
    <div class="z-10 flex w-full flex-row items-center justify-center gap-4 px-5 sm:px-[156px]">
      {buildArrow()}
      <span class="text-nowrap text-normal text-basePrimaryMedium">
        {props.text ?? "Get gift card(s) from choice of brands"}
      </span>

      <div class="w-full rotate-180">{buildArrow()}</div>
    </div>
  );

  function buildArrow() {
    return (
      <div
        class="h-[1px] w-full "
        style={{
          "border-radius": "2px",
          background:
            "linear-gradient(270deg, #313538 0%, rgba(49, 53, 56, 0.00) 100%)",
        }}
      >
        &nbsp;
      </div>
    );
  }
}
